import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save, RotateCcw, Sparkles, Image as ImageIcon, MessageSquare, Wand2 } from "lucide-react";

const PROMPT_META = {
  image_content: {
    title: "Content prompt (1:1 square)",
    description: "Wraps the user's prompt for AI Content images: generated square, then padded to every placement.",
    placeholders: ["user_prompt"],
    group: "image",
  },
  image_banner: {
    title: "Banner prompt (320 × 100)",
    description: "Wraps the user's prompt for banner creatives.",
    placeholders: ["user_prompt"],
    group: "image",
  },
  image_panel: {
    title: "Panel prompt (1920 × 1080)",
    description: "Wraps the user's prompt for 16:9 panel creatives.",
    placeholders: ["user_prompt"],
    group: "image",
  },
  deal_suggestion_system: {
    title: "System instruction",
    description: "High-level role / style guidance for the deal-suggestion model.",
    placeholders: [],
    group: "deal",
  },
  deal_suggestion_user: {
    title: "User prompt",
    description: "The actual request sent to the model. Must end with the JSON format block, because the parser depends on it.",
    placeholders: ["business_name", "category_text", "location_text"],
    group: "deal",
  },
};

const PROMPT_ORDER = ["image_content", "image_banner", "image_panel", "deal_suggestion_system", "deal_suggestion_user"];

const INPUT_WRAPPER = "rounded-lg border bg-muted/40 p-4 space-y-3";
const FIELD_SURFACE = "bg-card";

export default function AIModels() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [available, setAvailable] = useState({ image: [], text: [] });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings/ai-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConfig(res.data.config);
      setDefaults(res.data.defaults);
      // available_models.edit is deliberately ignored (round 8, W5). Editing runs on
      // image_model; a separate edit model was vestigial and nothing read it.
      setAvailable(res.data.available_models || { image: [], text: [] });
      setDirty(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load AI configuration");
    } finally {
      setLoading(false);
    }
  };

  const updateModel = (kind, value) => {
    setConfig((prev) => ({ ...prev, [`${kind}_model`]: value }));
    setDirty(true);
  };

  const updatePrompt = (key, value) => {
    setConfig((prev) => ({ ...prev, prompts: { ...prev.prompts, [key]: value } }));
    setDirty(true);
  };

  const resetPromptToDefault = (key) => {
    if (!defaults?.prompts?.[key]) return;
    updatePrompt(key, defaults.prompts[key]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/settings/ai-config`,
        {
          image_model: config.image_model,
          text_model: config.text_model,
          prompts: config.prompts,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfig(res.data.config);
      setDirty(false);
      toast.success("AI configuration saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const imagePrompts = useMemo(() => PROMPT_ORDER.filter((k) => PROMPT_META[k].group === "image"), []);
  const dealPrompts = useMemo(() => PROMPT_ORDER.filter((k) => PROMPT_META[k].group === "deal"), []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!config) return null;

  const renderModelSelect = (kind, options) => (
    <Select value={config[`${kind}_model`]} onValueChange={(v) => updateModel(kind, v)}>
      <SelectTrigger className={`w-full ${FIELD_SURFACE}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.id} value={opt.id}>
            <div className="flex flex-col items-start py-0.5">
              <div className="flex items-center gap-2">
                <span className="font-medium">{opt.label}</span>
                <span className="text-xs font-mono text-muted-foreground">{opt.id}</span>
              </div>
              {opt.note && <span className="text-xs text-muted-foreground">{opt.note}</span>}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const renderPromptField = (key) => {
    const meta = PROMPT_META[key];
    const isDefault = config.prompts[key] === defaults?.prompts?.[key];
    return (
      <div key={key} className={INPUT_WRAPPER}>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <Label className="text-sm font-semibold">{meta.title}</Label>
            <p className="text-xs text-muted-foreground leading-relaxed">{meta.description}</p>
            {meta.placeholders.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 pt-1">
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Placeholders:</span>
                {meta.placeholders.map((p) => (
                  <Badge
                    key={p}
                    variant="secondary"
                    className="font-mono text-[11px] py-0 h-5"
                  >
                    {`{${p}}`}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => resetPromptToDefault(key)}
            disabled={isDefault}
            className="shrink-0"
            title="Reset to hardcoded default"
          >
            <RotateCcw className="h-3 w-3 mr-1" /> Reset
          </Button>
        </div>
        <Textarea
          value={config.prompts[key] || ""}
          onChange={(e) => updatePrompt(key, e.target.value)}
          rows={key.startsWith("image_") ? 7 : 11}
          className={`font-mono text-xs leading-relaxed ${FIELD_SURFACE}`}
          spellCheck={false}
        />
      </div>
    );
  };

  const currentImageModel = available.image.find((m) => m.id === config.image_model);
  const currentTextModel = available.text.find((m) => m.id === config.text_model);

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto">
      <div className="flex items-start justify-between gap-4 pb-2 border-b">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Sparkles className="h-6 w-6 text-primary" /> AI Models &amp; Prompts
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Pick which Google models power image and deal-suggestion generation, and tune the prompt scaffolding around user input. Saves apply instantly - no redeploy.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" onClick={fetchConfig} disabled={saving}>Reload</Button>
          <Button onClick={handleSave} disabled={!dirty || saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save changes
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Models</CardTitle>
          <CardDescription>Only Google-supported IDs are listed; deprecation status is noted inline.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className={INPUT_WRAPPER}>
            <div className="space-y-1">
              <Label className="flex items-center gap-1.5 text-sm font-semibold">
                <ImageIcon className="h-3.5 w-3.5" /> Image generation
              </Label>
              <p className="text-xs text-muted-foreground">
                Used by the Content builder to generate <em>and</em> edit images (Nano Banana / Nano Banana Pro, US/global region).
              </p>
            </div>
            {renderModelSelect("image", available.image)}
            {currentImageModel?.note && (
              <p className="text-xs text-muted-foreground italic">{currentImageModel.note}</p>
            )}
          </div>

          <div className={INPUT_WRAPPER}>
            <div className="space-y-1">
              <Label className="flex items-center gap-1.5 text-sm font-semibold">
                <MessageSquare className="h-3.5 w-3.5" /> Text generation
              </Label>
              <p className="text-xs text-muted-foreground">
                Used by the "AI Suggest Deal" button on the campaign form.
              </p>
            </div>
            {renderModelSelect("text", available.text)}
            {currentTextModel?.note && (
              <p className="text-xs text-muted-foreground italic">{currentTextModel.note}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ImageIcon className="h-4 w-4" /> Image prompt templates
          </CardTitle>
          <CardDescription>
            Wrap the user's prompt with composition guidance. <code className="rounded bg-muted px-1 py-0.5 text-[11px]">{"{user_prompt}"}</code> is substituted at request time.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {imagePrompts.map(renderPromptField)}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="h-4 w-4" /> Deal-suggestion prompt templates
          </CardTitle>
          <CardDescription>
            System instruction sets the AI's role; user prompt is the actual request with business-specific placeholders. Keep the JSON format block intact.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {dealPrompts.map(renderPromptField)}
        </CardContent>
      </Card>

      {dirty && (
        <div className="sticky bottom-4 flex justify-end">
          <div className="rounded-full border bg-background/95 backdrop-blur shadow-lg px-2 py-1">
            <Button onClick={handleSave} disabled={saving} size="lg" className="rounded-full">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save changes
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
